/**
 * Decides — on the desktop build only — whether to show the one-time setup wizard
 * or the normal application. On the web build it renders children unchanged.
 *
 * A `localStorage` mirror (`setup_completed`) gives returning users an instant
 * decision with no flash; the authoritative answer comes from the main process
 * (`setup.getState()`) and corrects the mirror if they ever disagree.
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { isDesktop, setupApi } from './desktopApi';
import SetupWizard from './SetupWizard';

const MIRROR_KEY = 'setup_completed';

function FullscreenLoader() {
  return (
    <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <CircularProgress />
    </Box>
  );
}

export default function SetupGate({ children }) {
  const enabled = isDesktop() && !!setupApi;

  const initial = () => {
    if (!enabled) return 'ready';
    return window.localStorage.getItem(MIRROR_KEY) === 'true' ? 'ready' : 'checking';
  };
  const [status, setStatus] = useState(initial);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const state = await setupApi.getState();
        if (cancelled) return;
        const done = !!(state && state.completed);
        window.localStorage.setItem(MIRROR_KEY, done ? 'true' : 'false');
        setStatus(done ? 'ready' : 'wizard');
      } catch (err) {
        // Fail open: if the bridge errors, don't trap the user — show the app.
        // eslint-disable-next-line no-console
        console.error('[setup] getState failed', err);
        if (!cancelled) setStatus('ready');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (status === 'checking') return <FullscreenLoader />;
  if (status === 'wizard') {
    return (
      <SetupWizard
        onComplete={() => {
          window.localStorage.setItem(MIRROR_KEY, 'true');
          setStatus('ready');
        }}
      />
    );
  }
  return children;
}
